import {
  SCREENER_STATUSES,
  type ScreenerFlowEvent,
  type ScreenerFlowState,
  type ScreenerSession,
  type ScreenerStartLatch,
} from '@/shared-responder-v2-core';

import {
  GATEWAY_ERROR_KINDS,
  ResponderGatewayError,
} from './shared-responder-v2-gateway-error.helpers';

export function syncScreenerLatch(
  latch: ScreenerStartLatch,
  key: string
): void {
  if (latch.key === key) {
    return;
  }

  latch.key = key;
  latch.isInFlight = false;
  latch.hasResolved = false;
}

export function toScreenerStartFailureEvent(error: unknown): ScreenerFlowEvent {
  if (
    error instanceof ResponderGatewayError &&
    error.kind === GATEWAY_ERROR_KINDS.ScreenerNotAvailable
  ) {
    return { type: 'bypassed' };
  }

  return { type: 'failed' };
}

export function toScreenerKeyRejectionEvent(
  state: ScreenerFlowState
): ScreenerFlowEvent {
  return state.status === SCREENER_STATUSES.Completed
    ? { type: 'restarted' }
    : { type: 'failed' };
}

export async function resolveScreenerStart(
  latch: ScreenerStartLatch,
  input: {
    readonly key: string;
    readonly hasScreener: boolean;
    readonly isDeferred?: boolean;
    readonly storedInsightId: string | null;
  },
  startSession: () => Promise<ScreenerSession>,
  dispatch: (event: ScreenerFlowEvent) => void,
  failureLogMessage = 'Failed to start the screener session'
): Promise<void> {
  syncScreenerLatch(latch, input.key);

  if (latch.hasResolved || latch.isInFlight || input.isDeferred === true) {
    return;
  }

  if (!input.hasScreener || input.storedInsightId !== null) {
    latch.hasResolved = true;
    dispatch({ type: 'bypassed' });
    return;
  }

  latch.isInFlight = true;

  try {
    const session = await startSession();

    if (latch.key !== input.key) {
      return;
    }
    latch.hasResolved = true;
    dispatch({ type: 'sessionStarted', session });
  } catch (error) {
    if (latch.key !== input.key) {
      return;
    }
    latch.hasResolved = true;

    const event = toScreenerStartFailureEvent(error);

    if (event.type === 'failed') {
      console.error(failureLogMessage, error);
    }
    dispatch(event);
  } finally {
    if (latch.key === input.key) {
      latch.isInFlight = false;
    }
  }
}
